import React, { useState } from 'react'
import { Form, Table } from "react-bootstrap"
import {
  CircularProgress,
  MenuItem,
  Select,
  Box,
  Typography,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TextField,
  Button,
  IconButton,
  Modal
} from '@mui/material'
import { HighlightOff, DeleteForever, Save } from '@mui/icons-material'

import { ColorLibObj } from '../../../utils/types'
import { put, post, remove } from '../../../utils/http'
import { modalStyle } from '../../../styles/js/common'
import { isPrivate } from '../../../utils/config'

const LoadingBtn = () => {
  return (
    <button className='btn text-secondary btn-light' type='button' disabled>
      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
      &nbsp;&nbsp;Checking...
    </button>
  )
}

interface AnnotationClassProps {
  addNewTag: boolean,  
  handleNewTagClose: Function,
  colorLib: ColorLibObj[],
  updateFromAnnoClassLibrary: Function,
  removeFromAnnoClassLibrary: Function
}

const AnnotationClassModal = ({
  addNewTag, 
  handleNewTagClose,  
  colorLib, 
  updateFromAnnoClassLibrary,
  removeFromAnnoClassLibrary
}: AnnotationClassProps) => {
  const [enableEditable, setEnableEditable] = useState<boolean>(false)
  const [deleteClassModal, setDeleteClassModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [newClass, setNewClass] = useState<boolean>(false)
  const [classForm, setClassForm] = useState({
    id: '',
    category: 'prostate cancer',
    name: '',
    desc: '',
    color: '#000000'
  })

  const userRole = localStorage.getItem('role') || ''
  const isAdmin = ['dev', 'admin', 'superadmin'].includes(userRole)

  const closeDeleteClassModal = () => setDeleteClassModal(false)

  const editClass = (data: any) => {
    setNewClass(false)
    setClassForm(data)
    setEnableEditable(true)
  }

  const cancelEdit = () => {
    setClassForm({id: '', category: 'prostate cancer', name: '', desc: '', color: '#000000'})
    setEnableEditable(false)
  }

  const saveNewClass = async () => {
    const paylod = {
      category: classForm.category,
      name: classForm.name.trim(),
      color: classForm.color.length ? classForm.color : '#000000',
      desc: classForm.desc ? classForm.desc.trim() : ''
    }
    if (!paylod.category || !paylod.name.length || !paylod.color) return
    setIsLoading(true)
    try {
      if (!enableEditable) await post('Annotationlibs/', paylod).then(res => updateFromAnnoClassLibrary(res))
      else {
        await put(`Annotationlibs/${classForm.id}`, paylod).then(res => updateFromAnnoClassLibrary(res))
        setEnableEditable(false)
      }
    } catch (err) {
      console.log(err)
    } finally {
      cancelNew()
      setIsLoading(false)
    }
  }

  const openDeleteClassModal = () => setDeleteClassModal(true)

  const deleteAnnotationClass = async () => {
    setIsLoading(true)
    try {
      await remove(`Annotationlibs/${classForm.id}`)
      removeFromAnnoClassLibrary(classForm.id)
      setClassForm({id: '', category: 'prostate cancer', name: '', desc: '', color: '#000000'})
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
      setEnableEditable(false)
      setDeleteClassModal(false)
    }
  }

  const addNewClass = () => {
    setClassForm({id: '', category: 'prostate cancer', name: '', desc: '', color: '#000000'})
    setNewClass(true)
  }

  const cancelNew = () => {
    setClassForm({id: '', category: 'prostate cancer', name: '', desc: '', color: '#000000'})
    setNewClass(false)
  }

  const handleChange = (e: any) => setClassForm(prevState => ({...prevState, [e.target.name]: e.target.value}))

  const closeModal = () => {
    setNewClass(false)
    handleNewTagClose()
  }
  
  return (
    <>
      <Modal open={addNewTag} onClose={closeModal}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4' sx={{maxWidth: 900, width: '50vw'}}>
            <Typography variant="h5" component="h1">Annotation Class Menu</Typography>
            <Box>
              {(isAdmin || isPrivate) && <Button className='mb-3' variant="contained" onClick={addNewClass}>New Class</Button>}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Class Name</TableCell>
                      <TableCell>Color</TableCell>
                      {(isAdmin || isPrivate) && <TableCell></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newClass &&
                      <TableRow>
                        <TableCell>
                          <Select
                            labelId="class-catg-select-label"
                            value={classForm.category}
                            label="Category"
                            name='category'
                            onChange={handleChange}
                            variant="standard"
                          >
                            <MenuItem value="prostate cancer">Prostate Cancer</MenuItem>
                            <MenuItem value="breast cancer">Breast Cancer</MenuItem>
                            <MenuItem value="lung cancer">Lung Cancer</MenuItem>
                            <MenuItem value="bladder cancer">Bladder Cancer</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" name='name' value={classForm.name} onChange={handleChange}/>
                        </TableCell>
                        <TableCell>
                          <Form.Control name="color" type="color" onChange={handleChange} value={classForm.color} />
                        </TableCell>
                        <TableCell>
                          {isLoading
                            ? <CircularProgress size={24} />
                            : <>
                              <IconButton title='Cancel' onClick={cancelNew}><HighlightOff /></IconButton>
                              <IconButton color="primary" onClick={saveNewClass}><Save /></IconButton>
                            </>
                          }
                        </TableCell>
                      </TableRow>
                    }
                    {colorLib.map((i:any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          {i.id === classForm.id && enableEditable
                            ? <Select
                              labelId="class-catg-select-label"
                              value={classForm.category}
                              label="Category"
                              name='category'
                              onChange={handleChange}
                              variant="standard"
                            >
                              <MenuItem value="prostate cancer">Prostate Cancer</MenuItem>
                              <MenuItem value="breast cancer">Breast Cancer</MenuItem>
                              <MenuItem value="lung cancer">Lung Cancer</MenuItem>
                              <MenuItem value="bladder cancer">Bladder Cancer</MenuItem>
                            </Select>
                            : i.category
                          }
                        </TableCell>
                        <TableCell>
                          {i.id === classForm.id && enableEditable
                            ? <TextField variant="standard" name='name' value={classForm.name} onChange={handleChange} />
                            : i.name
                          }
                        </TableCell>
                        <TableCell>
                          {i.id === classForm.id && enableEditable
                            ? <Form.Control name="color" type="color" onChange={handleChange} value={classForm.color} />
                            : <Box className='text-center' sx={{backgroundColor: i.color}}>{i.color}</Box>
                          }
                        </TableCell>
                        {(isAdmin || isPrivate) && <TableCell>
                          {i.id === classForm.id
                            ? isLoading 
                              ? <CircularProgress size={24} />
                              : <Box className='d-flex gap-2'>
                                <IconButton title='Cancel' onClick={cancelEdit}><HighlightOff /></IconButton>
                                <IconButton title='Save' color="primary" onClick={saveNewClass}><Save /></IconButton>
                                <IconButton title='Delete' color="error" onClick={openDeleteClassModal}><DeleteForever /></IconButton>
                              </Box>
                            : <Button variant="text" onClick={() => editClass(i)} size="small">Edit</Button>
                          }
                        </TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
          <Modal open={deleteClassModal} onClose={closeDeleteClassModal}>
            <Box sx={modalStyle}>
              <Box className='d-flex flex-column gap-4'>
                <Typography variant="h5" component="h1">Delete class from library ?</Typography>
                <Typography>Confirm to delete selected class</Typography>
                <Box className='d-flex gap-3 justify-content-end'>
                  {isLoading
                    ? <CircularProgress size={28}/>
                    : <>
                      <Button variant="outlined" onClick={closeDeleteClassModal}>Close</Button>
                      <Button variant="contained" color="error" onClick={deleteAnnotationClass}>Confirm</Button>
                    </>
                  }
                </Box>
              </Box>
            </Box>
          </Modal>
        </Box>
      </Modal>
    </>
  )
}

interface MapWarnProps { text: string | null | undefined, loader?: boolean }

const MapWarningTexts = ({ text, loader = false }: MapWarnProps) => {
  return (
    <Box sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: 1,
      color: 'grey'
    }}>
      {loader
        ? <CircularProgress />
        : <Typography variant='h5'>{text || 'Error'}</Typography>
      }
      
    </Box>
  )
}

export {
  LoadingBtn,
  AnnotationClassModal,
  MapWarningTexts
}